import { createSlice } from "@reduxjs/toolkit";
import type { SubscriptionSaleItemProto } from "@noom/noom-contracts/noom_contracts/billing/product_catalog/subscription_sale_item";
import { OfferFlowType, ProductOffer, PurchaseType } from "gql/graphql";

import { getTrackingParams } from "src/api/utils";
import { productSearch } from "src/utils/api/productSearch";
import { createOrderRequest } from "src/utils/api/purchase/ordersCheckoutApi";
import { getProductOffers } from "src/utils/api/purchase/productOffers";
import { captureException } from "src/utils/error";
import { createAppAsyncThunk } from "../async-thunk";
import getStore, { type AppDispatch } from "../store";
import { type MedProductName } from "@utils/medProductNames";
import { OrderOperationProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/order_operation";
import { OrderProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/orders_service";
import { convertGqlOrderOperationToProto } from "@utils/orderConversions";
import { getActiveUser } from "@noom/hive-auth";

export type MedicationSelectionOfferVariant = {
  orderOperation: OrderOperationProto;
  /** Undefined when the user aware search did not return the sale item. */
  saleItem: SubscriptionSaleItemProto;
  /** Preview order, populated by `fetchMedicationSelectionOfferOrders`. */
  order?: OrderProto;
};

export type MedicationSelectionOffer = {
  productName: string;
  purchaseType: PurchaseType;
  eligibleVariants: MedicationSelectionOfferVariant[];
  ineligibleVariants: MedicationSelectionOfferVariant[];
  ordersLoadStatus: "idle" | "loading" | "success" | "failure";
};

export type MedicationSelectionOffersByProduct = Partial<
  Record<MedProductName, MedicationSelectionOffer>
>;

export type MedicationSelectionOffersState = {
  loadStatus: "idle" | "loading" | "success" | "failure";
  offers: MedicationSelectionOffersByProduct;
};

const initialState: MedicationSelectionOffersState = {
  loadStatus: "idle",
  offers: {},
};

export function buildMedicationSelectionOffers(
  offers: ProductOffer[],
  ineligibleOffers: ProductOffer[],
  saleItems: SubscriptionSaleItemProto[]
): MedicationSelectionOffersByProduct {
  const saleItemsById = new Map(
    saleItems.map((saleItem) => [saleItem.saleItemId, saleItem])
  );
  const result: MedicationSelectionOffersByProduct = {};

  // A product name can appear in both lists (e.g. an eligible 5mg dose and an
  // ineligible 10mg dose), so both lists merge into the same entry.
  function addVariants(
    offer: ProductOffer,
    list: "eligibleVariants" | "ineligibleVariants"
  ) {
    if (!result[offer.productName]) {
      result[offer.productName] = {
        productName: offer.productName,
        purchaseType: offer.purchaseType,
        eligibleVariants: [],
        ineligibleVariants: [],
        ordersLoadStatus: "idle",
      };
    }
    result[offer.productName][list].push(
      ...offer.variants.map(({ orderOperation }) => {
        const saleItem = saleItemsById.get(orderOperation.saleItemId);
        if (!saleItem) {
          throw new Error(`SaleItem ${orderOperation.saleItemId} not found`);
        }
        return {
          orderOperation: convertGqlOrderOperationToProto(orderOperation),
          saleItem,
        };
      })
    );
  }

  offers.forEach((offer) => addVariants(offer, "eligibleVariants"));
  ineligibleOffers.forEach((offer) => addVariants(offer, "ineligibleVariants"));

  return result;
}

export const fetchMedicationSelectionOffers = createAppAsyncThunk<
  void,
  MedicationSelectionOffersByProduct
>("medicationSelectionOffers/fetchMedicationSelectionOffers", async () => {
  try {
    const { offers, ineligibleOffers } = await getProductOffers({
      offerFlowType: OfferFlowType.MembershipMedSelection,
      trackingParams: getTrackingParams(),
    });

    const productNames = [
      ...new Set(
        [...offers, ...ineligibleOffers].map((offer) => offer.productName)
      ),
    ];

    if (productNames.length === 0) {
      return {};
    }

    const { subscriptionSaleItems } = await productSearch(productNames);

    return buildMedicationSelectionOffers(
      offers,
      ineligibleOffers,
      subscriptionSaleItems
    );
  } catch (error) {
    captureException(error, "fetchMedicationSelectionOffers");
    throw error;
  }
});

/**
 * Creates a preview order for eligible variants of the given product if
 * they are sellable and have a promo code attached to the operation.
 * Attaches the order preview to the variant if created.
 */
export const fetchMedicationSelectionOfferOrders = createAppAsyncThunk<
  MedProductName,
  {
    productName: MedProductName;
    eligibleVariants: MedicationSelectionOfferVariant[];
    ineligibleVariants: MedicationSelectionOfferVariant[];
  }
>(
  "medicationSelectionOffers/fetchMedicationSelectionOfferOrders",
  async (productName, { getState }) => {
    try {
      const offer = getState().medicationSelectionOffers.offers[productName];
      if (!offer) {
        throw new Error(`No medication selection offer for ${productName}`);
      }
      const accessCode = getActiveUser()?.accessCode;
      const [eligibleVariants, ineligibleVariants] = await Promise.all([
        Promise.all(
          offer.eligibleVariants.map(async (variant) => ({
            ...variant,
            order:
              variant.saleItem.isSellable &&
              variant.orderOperation.purchaseOperation?.promoCode &&
              (await createOrderRequest(
                variant.orderOperation,
                accessCode,
                true
              )),
          }))
        ),
        Promise.all(
          offer.ineligibleVariants.map(async (variant) => ({
            ...variant,
            order:
              variant.saleItem.isSellable &&
              variant.orderOperation.purchaseOperation?.promoCode &&
              (await createOrderRequest(
                variant.orderOperation,
                undefined,
                true
              )),
          }))
        ),
      ]);

      return { productName, eligibleVariants, ineligibleVariants };
    } catch (error) {
      captureException(error, "fetchMedicationSelectionOfferOrders");
      throw error;
    }
  }
);

const medicationSelectionOffersSlice = createSlice({
  name: "medicationSelectionOffers",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMedicationSelectionOffers.pending, (state) => ({
        ...state,
        loadStatus: "loading",
      }))
      .addCase(fetchMedicationSelectionOffers.fulfilled, (_state, action) => ({
        loadStatus: "success",
        offers: action.payload,
      }))
      .addCase(fetchMedicationSelectionOffers.rejected, () => ({
        loadStatus: "failure",
        offers: {},
      }))
      .addCase(fetchMedicationSelectionOfferOrders.pending, (state, action) => {
        const { arg: productName } = action.meta;
        const offer = state.offers[productName];
        if (offer) {
          offer.ordersLoadStatus = "loading";
        }
      })
      .addCase(
        fetchMedicationSelectionOfferOrders.rejected,
        (state, action) => {
          const { arg: productName } = action.meta;
          const offer = state.offers[productName];
          if (offer) {
            offer.ordersLoadStatus = "failure";
          }
        }
      )
      .addCase(
        fetchMedicationSelectionOfferOrders.fulfilled,
        (state, action) => {
          const { productName, eligibleVariants, ineligibleVariants } =
            action.payload;
          const offer = state.offers[productName];
          if (offer) {
            offer.eligibleVariants = eligibleVariants;
            offer.ineligibleVariants = ineligibleVariants;
            offer.ordersLoadStatus = "success";
          }
        }
      );
  },
});

export default medicationSelectionOffersSlice;

let pendingLoad: Promise<void> | null = null;

/**
 * Loads the medication selection offers into Redux. Concurrent calls share
 * the in-flight load. The returned promise always resolves; read `loadStatus`
 * for the outcome.
 */
export function loadMedicationSelectionOffers(
  dispatch: AppDispatch = getStore().dispatch
): Promise<void> {
  if (!pendingLoad) {
    pendingLoad = dispatch(fetchMedicationSelectionOffers())
      .then(() => undefined)
      .finally(() => {
        pendingLoad = null;
      });
  }
  return pendingLoad;
}

/** Resolves once the in-flight load (if any) completes. */
export function waitForMedicationSelectionOffers(): Promise<void> {
  return pendingLoad ?? Promise.resolve();
}
